import type { Industry, Item } from "./types";

export type ItemShelf = {
  id: string;
  label: string;
  shortLabel: string;
  description: string;
  categories: string[];
  recommendedSlugs: string[];
};

export const ITEM_SHELVES: ItemShelf[] = [
  {
    id: "apparel_towel",
    label: "定番アパレル・タオル",
    shortLabel: "アパレル",
    description: "ライブ物販・イベント・販促で最初に提案しやすい定番品。",
    categories: ["apparel", "idol_apparel_towel"],
    recommendedSlugs: ["tshirt", "towel"],
  },
  {
    id: "live_essential",
    label: "ライブ必需品・応援グッズ",
    shortLabel: "応援",
    description: "会場で使う前提のグッズ。推し活・スポーツ・イベント向け。",
    categories: ["idol_live_essential"],
    recommendedSlugs: [],
  },
  {
    id: "acrylic_keyholder",
    label: "アクリル・キーホルダー類",
    shortLabel: "アクリル",
    description: "絵柄展開しやすく、画像サンプルから話を始めやすい棚。",
    categories: ["acrylic", "idol_acrylic_keyholder"],
    recommendedSlugs: ["acrylic-stand", "keyholder"],
  },
  {
    id: "photo_card",
    label: "写真・カード類",
    shortLabel: "写真・カード",
    description: "ランダム/コレクション性で単価より回転を作るカテゴリ。",
    categories: ["idol_photo_card"],
    recommendedSlugs: ["clear-file"],
  },
  {
    id: "badge_sticker",
    label: "缶バッジ・ステッカー",
    shortLabel: "バッジ",
    description: "低単価・多種展開・追加提案に向くカテゴリ。",
    categories: ["idol_badge_sticker"],
    recommendedSlugs: ["can-badge", "sticker"],
  },
  {
    id: "plush_nui",
    label: "ぬいぐるみ・ぬい活",
    shortLabel: "ぬい活",
    description: "高単価化しやすく、ファンコミュニティに刺さるカテゴリ。",
    categories: ["plush", "idol_plush_nui"],
    recommendedSlugs: ["plush"],
  },
  {
    id: "lifestyle",
    label: "生活雑貨・日用品",
    shortLabel: "生活雑貨",
    description: "普段使いの接点を増やす、施設・小売・ノベルティ向けの棚。",
    categories: ["tableware", "idol_lifestyle_daily"],
    recommendedSlugs: ["mug", "tumbler", "clear-bottle"],
  },
  {
    id: "stationery",
    label: "文具・季節商材",
    shortLabel: "文具",
    description: "単価を抑えつつ提案書に入れやすい実用品。",
    categories: ["idol_stationery_seasonal"],
    recommendedSlugs: ["ballpoint", "clear-file"],
  },
  {
    id: "bag_storage",
    label: "バッグ・収納",
    shortLabel: "バッグ",
    description: "購入後の使用シーンが見えやすく、物販にも販促にも使いやすい棚。",
    categories: ["idol_bag_storage"],
    recommendedSlugs: ["tote-bag"],
  },
  {
    id: "premium_accessory",
    label: "アクセサリー・高単価",
    shortLabel: "高単価",
    description: "限定品・受注生産・記念品の提案に向くカテゴリ。",
    categories: ["idol_premium_accessory"],
    recommendedSlugs: [],
  },
  {
    id: "smartphone",
    label: "スマホ周辺",
    shortLabel: "スマホ",
    description: "日常接触が多く、若年層向け提案で使いやすいカテゴリ。",
    categories: ["idol_smartphone"],
    recommendedSlugs: [],
  },
  {
    id: "special_finish",
    label: "特殊加工・付加価値",
    shortLabel: "加工",
    description: "価格勝負から外して、質感や限定感で提案するカテゴリ。",
    categories: ["idol_special_finish"],
    recommendedSlugs: [],
  },
  {
    id: "digital_experience",
    label: "デジタル・体験型",
    shortLabel: "体験",
    description: "リアルグッズにデジタル特典や抽選体験を重ねる提案。",
    categories: ["idol_digital_experience"],
    recommendedSlugs: [],
  },
  {
    id: "package_operation",
    label: "什器・パッケージ・運営",
    shortLabel: "運営",
    description: "売り場・発送・特典封入まで含めて提案するカテゴリ。",
    categories: ["idol_package_operation"],
    recommendedSlugs: ["paper-bag", "clear-case"],
  },
];

export const INDUSTRY_TONE: Record<
  string,
  { title: string; prompt: string; sellingAngle: string }
> = {
  fancy_stationery: {
    title: "かわいい売り場映え",
    prompt: "パステル基調、雑貨店の棚に置きやすい上品でかわいい配色、白背景の商品写真",
    sellingAngle: "小売棚で手に取りやすい見た目を先に作る",
  },
  sales_promotion: {
    title: "企業ロゴが映える",
    prompt: "清潔感のある企業ノベルティ、白背景、ロゴを入れやすい余白、量産しやすい仕様",
    sellingAngle: "単価と納期を見せながら法人提案に寄せる",
  },
  sports_event: {
    title: "チームカラー訴求",
    prompt: "スポーツイベント向け、鮮やかなチームカラー、会場で使うシーンが想像できる商品写真",
    sellingAngle: "応援・来場記念・チーム物販を一緒に見せる",
  },
  anime_content: {
    title: "推し活・コレクション",
    prompt: "推し活向け、彩度高め、コレクションしたくなる限定グッズ感、アニメ/アイドル物販の雰囲気",
    sellingAngle: "複数種展開とランダム/限定感で単価を作る",
  },
  facility_org: {
    title: "施設土産・ファミリー",
    prompt: "博物館・水族館・観光施設の土産向け、親しみやすく清潔な商品写真、家族向け",
    sellingAngle: "来場記念と館内売店での買いやすさを重視する",
  },
  retail: {
    title: "PB・棚展開",
    prompt: "リテール向け、パッケージ感のある商品写真、棚に並べた時に統一感が出るデザイン",
    sellingAngle: "シリーズ化・陳列・季節差し替えを提案する",
  },
};

export function getShelfForItem(item: Item | null | undefined) {
  if (!item) return ITEM_SHELVES[0];
  return (
    ITEM_SHELVES.find(
      (shelf) =>
        shelf.recommendedSlugs.includes(item.slug) ||
        (item.category != null && shelf.categories.includes(item.category)),
    ) ?? ITEM_SHELVES[0]
  );
}

export function getItemsForShelf(items: Item[], shelfId: string) {
  if (shelfId === "all") return items;
  const shelf = ITEM_SHELVES.find((s) => s.id === shelfId);
  if (!shelf) return items;
  const matched = items.filter(
    (item) =>
      shelf.recommendedSlugs.includes(item.slug) ||
      (item.category != null && shelf.categories.includes(item.category)),
  );
  return matched.length > 0 ? matched : items.filter((item) => shelf.recommendedSlugs.includes(item.slug));
}

export function industryLabel(industry: Industry | null | undefined) {
  return industry?.sub_name ?? "業界未選択";
}

export function industryTone(industry: Industry | null | undefined) {
  if (!industry) return null;
  return INDUSTRY_TONE[industry.category] ?? null;
}
